import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Plus, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { api, eur } from "@/lib/api";

let summaryPromise = null;
const loadSummary = () => {
  if (!summaryPromise) summaryPromise = api.reviewSummary().catch(() => ({}));
  return summaryPromise;
};

export default function ProductCard({ product, index = 0 }) {
  const { localize, t } = useLanguage();
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const loc = localize(product.i18n);
  const [rating, setRating] = useState(null);
  useEffect(() => {
    let on = true;
    loadSummary().then((s) => { if (on) setRating(s[product.slug] || null); });
    return () => { on = false; };
  }, [product.slug]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${product.slug}`}
      className="group card-lift relative border border-navy/10 bg-white"
    >
      <Link to={`/product/${product.slug}`} className={`img-zoom-frame frame-corners relative block aspect-[4/5] border-b border-navy/10 ${product.coming_soon ? "bg-[#0c0c0e]" : "bg-surface"}`}>
        <img src={product.image} alt={loc.name} className={`h-full w-full ${product.coming_soon ? "object-contain" : "object-cover"}`} loading="lazy" />
        {product.badge && (
          <span data-testid={`product-badge-${product.slug}`} className="absolute start-4 top-4 bg-gold px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-navy">
            {t.featured.badge}
          </span>
        )}
        {product.coming_soon && (
          <span data-testid={`product-soon-${product.slug}`} className={`absolute top-4 ${product.badge ? "start-4 mt-9" : "start-4"} bg-navy px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-gold`}>
            {t.prod.soonBadge}
          </span>
        )}
      </Link>
      <button
        data-testid={`wish-toggle-${product.slug}`}
        onClick={() => toggle(product.slug)}
        className={`absolute end-3 top-3 z-10 flex h-9 w-9 items-center justify-center border bg-white/90 backdrop-blur-sm transition-colors duration-300 ${has(product.slug) ? "border-gold text-gold-dark" : "border-navy/10 text-navy/40 hover:text-gold-dark"}`}
        aria-label="Toggle wishlist"
      >
        <Heart size={15} strokeWidth={1.75} fill={has(product.slug) ? "currentColor" : "none"} />
      </button>
      <div className="p-6">
        <p className="label-luxe text-sage-dark">{t.cats[`${product.category}_n`]}</p>
        <Link to={`/product/${product.slug}`}>
          <h3 className="mt-2 font-display text-xl font-medium text-navy transition-colors group-hover:text-gold-dark">
            {loc.name}
          </h3>
        </Link>
        <p className="mt-1 text-xs font-light text-navy/50">{loc.tagline}</p>
        {rating && rating.count > 0 && (
          <div data-testid={`product-rating-${product.slug}`} className="mt-2 flex items-center gap-1.5">
            <span className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={11} className={s <= Math.round(rating.avg) ? "fill-gold text-gold" : "text-navy/15"} />
              ))}
            </span>
            <span className="text-[10px] font-medium tracking-wide text-navy/50">
              {rating.avg.toFixed(1)} · {rating.count}
            </span>
          </div>
        )}
        <div className="mt-5 flex items-center justify-between">
          <span data-testid={`product-price-${product.slug}`} className="font-display text-lg font-medium text-navy">
            {eur(product.price)}
          </span>
          <button
            data-testid={`product-add-${product.slug}`}
            onClick={() => addItem(product)}
            className="flex items-center gap-2 border border-navy/15 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-all duration-500 hover:border-gold hover:bg-gold hover:text-navy lg:translate-y-1 lg:opacity-0 lg:group-hover:translate-y-0 lg:group-hover:opacity-100"
            aria-label={`${t.actions.addToCart} — ${loc.name}`}
          >
            <Plus size={13} strokeWidth={2} />
            {t.actions.addToCart}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
